// lifetime-thickness-v0 reader (docs/contracts/lifetime-thickness-v0.md): a compact normative reference for the viewport's live
// vertex layer, not a `sim` format. The run is ba-life's rutherford-thickness-v1 on pack fsaverage-destrieux-v1: Rutherford et al.
// 2022's predictive median cortical thickness per Destrieux parcel, ages 2 to 100 years since birth, one series a sex, and one
// exact parcel index per vertex of the pack's two cortex meshes (65535: no data, which expands to NaN and leaves the tissue's own
// colour). The app expands a sex's series at an age over the vertices by sample.mjs's rule, exactly (linear between the two annual
// medians around the age, a derived display operation), and for two ages subtracts the two expansions vertex by vertex.

import type { SimFiles } from './files.ts';
import type { ArrayDescriptor } from './binary.ts';

export type ThicknessSex = 'Female' | 'Male';
export const SEXES: ThicknessSex[] = ['Female', 'Male'];

/** One row of parcels.json: a Destrieux parcel's model name, its annotation and the vertices it covers. */
export interface ThicknessParcel {
  model: string;
  hemisphere: string;
  annotation_name: string;
  stats_name?: string;
  annotation_index?: number;
  rgba?: number[];
  vertices?: number;
  structure_id: string;
  model_index: number;
}

export interface ThicknessSurface {
  id: string;
  vertices: number;
  triangles?: number;
  vertex_offset: number;
  mesh_sha256?: string;
  no_data_vertices?: number;
}

export interface ThicknessHeader {
  format?: string;
  id?: string;
  domain?: string;
  kind?: string;
  space?: string;
  coordinates?: { axes?: string; units?: string; frame?: string };
  model?: { name?: string; type?: string; commit?: string; pcntoolkit?: string; site_convention?: string; fits?: number };
  pack?: { id?: string; version?: string };
  vertices?: number;
  structure_ids?: string[];
  surfaces?: ThicknessSurface[];
  age?: { quantity?: string; units?: string; values?: number[]; interpolation?: string; brainchart_post_conception_mapping?: unknown };
  time?: { base?: string; units?: string };
  units?: { thickness?: string };
  statistic?: string;
  sex_order?: string[];
  model_ids?: string[];
  series?: Partial<Record<ThicknessSex, ArrayDescriptor>>;
  vertex_model_index?: ArrayDescriptor & { no_data?: number };
  display?: { quantity?: string; label?: string; colormap?: string; range?: [number, number]; no_data?: string; geometry?: string };
  inputs?: Record<string, string>;
  pipeline?: { path?: string; commit?: string };
  methods?: { summary?: string; references?: string[] } | string;
  created_utc?: string;
}

/** Where an age falls between the annual medians: the lower row and the fraction towards the next (the last interval at the top end). */
export interface Bracket {
  lower: number;
  fraction: number;
}

export class ThicknessSim {
  private readonly seriesP = new Map<ThicknessSex, Promise<Float32Array>>();
  private indexP: Promise<Uint16Array> | null = null;
  private parcelsP: Promise<ThicknessParcel[]> | null = null;

  private constructor(
    readonly files: SimFiles,
    readonly header: ThicknessHeader,
    /** The annual ages, years since birth, consecutive (2, 3, … 100). */
    readonly ages: number[],
    /** The parcels' model names, in series order. */
    readonly modelIds: string[],
    /** The pack structures the vertices belong to, in concatenation order. */
    readonly structureIds: string[],
    /** Vertices over both structures. */
    readonly vertices: number,
  ) {}

  static async open(files: SimFiles): Promise<ThicknessSim> {
    const h = await files.json<ThicknessHeader>('header.json');
    const where = `sims/${files.dir}`;
    if (h.format !== 'lifetime-thickness-v0') throw new Error(`${where}: ${String(h.format)}, not lifetime-thickness-v0`);
    const ages = h.age?.values;
    if (!Array.isArray(ages) || ages.length < 2 || !ages.every((a, i) => typeof a === 'number' && a === ages[0]! + i)) throw new Error(`${where}: age.values must be consecutive years`);
    const ids = h.model_ids;
    if (!Array.isArray(ids) || !ids.length) throw new Error(`${where}: no model_ids`);
    const structs = h.structure_ids;
    if (!Array.isArray(structs) || !structs.length) throw new Error(`${where}: no structure_ids`);
    if (typeof h.vertices !== 'number' || !(h.vertices > 0)) throw new Error(`${where}: no vertex count`);
    if (!h.series || !SEXES.every((s) => h.series?.[s])) throw new Error(`${where}: a series for each of ${SEXES.join(' and ')} is needed`);
    if (!h.vertex_model_index) throw new Error(`${where}: no vertex_model_index`);
    return new ThicknessSim(files, h, ages, ids, structs, h.vertices);
  }

  /** The first and last age, years since birth. */
  get ageRange(): [number, number] {
    return [this.ages[0]!, this.ages[this.ages.length - 1]!];
  }

  get parcelCount(): number {
    return this.modelIds.length;
  }

  /** The vertex index that means no parcel (65535). */
  get noData(): number {
    return this.header.vertex_model_index?.no_data ?? 65535;
  }

  /** The vertices without a parcel, by the header's count per surface. */
  get noDataVertices(): number {
    return (this.header.surfaces ?? []).reduce((n, s) => n + (s.no_data_vertices ?? 0), 0);
  }

  /** The reference's own display range, mm. */
  get range(): [number, number] {
    const r = this.header.display?.range;
    return Array.isArray(r) && r.length === 2 && r[0] < r[1] ? [r[0], r[1]] : [1.5, 4];
  }

  get label(): string {
    return this.header.display?.label ?? 'Expected cortical thickness';
  }

  get unit(): string {
    return this.header.units?.thickness ?? 'mm';
  }

  /** A sex's medians, [ages × parcels] row-major, read once. */
  series(sex: ThicknessSex): Promise<Float32Array> {
    let p = this.seriesP.get(sex);
    if (!p) {
      p = this.files.array(this.header.series![sex]!).then((a) => {
        if (!(a instanceof Float32Array)) throw new Error(`sims/${this.files.dir}: ${sex}’s series isn’t f32`);
        if (a.length !== this.ages.length * this.parcelCount) throw new Error(`sims/${this.files.dir}: ${sex}’s series is ${a.length} values for ${this.ages.length} × ${this.parcelCount}`);
        return a;
      });
      this.seriesP.set(sex, p);
    }
    return p;
  }

  /** Each vertex's parcel (its model index), read once; `noData` where it has none. */
  index(): Promise<Uint16Array> {
    return (this.indexP ??= this.files.array(this.header.vertex_model_index!).then((a) => {
      if (!(a instanceof Uint16Array)) throw new Error(`sims/${this.files.dir}: vertex_model_index isn’t u16`);
      if (a.length !== this.vertices) throw new Error(`sims/${this.files.dir}: ${a.length} vertex indices for ${this.vertices} vertices`);
      return a;
    }));
  }

  /** parcels.json's rows; none when the run has none. */
  parcels(): Promise<ThicknessParcel[]> {
    return (this.parcelsP ??= this.files.maybeJson<ThicknessParcel[]>('parcels.json').then((p) => (Array.isArray(p) ? p : [])));
  }

  /** Where an age falls (sample.mjs's rule): refused outside the reference's ages. */
  bracket(age: number): Bracket {
    const [a0, a1] = this.ageRange;
    if (!Number.isFinite(age) || age < a0 || age > a1) throw new RangeError(`The thickness reference covers ages ${a0}–${a1} years since birth`);
    const lower = Math.min(this.ages.length - 2, Math.floor(age - a0));
    return { lower, fraction: age - a0 - lower };
  }

  /** The parcel medians at an age, one per model index: linear between the two annual medians around it. */
  async parcelMedians(sex: ThicknessSex, age: number): Promise<Float32Array> {
    const s = await this.series(sex);
    const n = this.parcelCount;
    const { lower, fraction } = this.bracket(age);
    const out = new Float32Array(n);
    for (let p = 0; p < n; p++) {
      const a = s[lower * n + p]!;
      const b = s[(lower + 1) * n + p]!;
      out[p] = a + fraction * (b - a);
    }
    return out;
  }

  /** The expansion over the pack's vertices: each vertex its parcel's median at the age; NaN where it has no parcel. */
  async sample(sex: ThicknessSex, age: number): Promise<Float32Array> {
    const at = this.bracket(age);
    const [s, idx] = await Promise.all([this.series(sex), this.index()]);
    return expand(s, idx, this.ages.length, this.parcelCount, at, this.noData);
  }

  /** Two ages' expansions subtracted vertex by vertex, B − A for one sex: the difference of published medians, exactly; NaN stays NaN. */
  async difference(sex: ThicknessSex, ageA: number, ageB: number): Promise<Float32Array> {
    const [a, b] = await Promise.all([this.sample(sex, ageA), this.sample(sex, ageB)]);
    const out = new Float32Array(a.length);
    for (let i = 0; i < a.length; i++) out[i] = b[i]! - a[i]!;
    return out;
  }

  /** The mean of the parcel medians at each age, equal weight: a summary of the reference for the strip, not a published statistic. */
  async meanCurve(sex: ThicknessSex): Promise<Float64Array> {
    const s = await this.series(sex);
    const n = this.parcelCount;
    const out = new Float64Array(this.ages.length);
    for (let i = 0; i < this.ages.length; i++) {
      let sum = 0;
      for (let p = 0; p < n; p++) sum += s[i * n + p]!;
      out[i] = sum / n;
    }
    return out;
  }
}

/** sample.mjs's expansion, exactly: `lower` and `fraction` from the age, NaN for a no-data vertex, a refusal for an index past the parcels. */
export function expand(series: ArrayLike<number>, index: ArrayLike<number>, ages: number, parcels: number, at: Bracket, noData = 65535): Float32Array {
  if (series.length !== ages * parcels) throw new Error('Reference arrays do not match header');
  const out = new Float32Array(index.length);
  const { lower, fraction } = at;
  for (let i = 0; i < out.length; i++) {
    const parcel = index[i]!;
    if (parcel === noData) out[i] = NaN;
    else {
      if (parcel >= parcels) throw new Error('Unknown Destrieux model index');
      const a = series[lower * parcels + parcel]!;
      const b = series[(lower + 1) * parcels + parcel]!;
      out[i] = a + fraction * (b - a);
    }
  }
  return out;
}

/** Of per-parcel values: the indices of the largest and smallest finite ones, how many are finite, and their equal-weight mean. */
export function parcelSummary(values: ArrayLike<number>): { max: number; min: number; finite: number; mean: number } {
  let max = -1;
  let min = -1;
  let finite = 0;
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    if (!Number.isFinite(v)) continue;
    finite++;
    sum += v;
    if (max < 0 || v > values[max]!) max = i;
    if (min < 0 || v < values[min]!) min = i;
  }
  return { max, min, finite, mean: finite ? sum / finite : NaN };
}
